import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { configuracoesApi, Configuracao, ConfiguracaoCreate, ConfiguracaoUpdate } from '../api/configuracoes'
import ActionBar from '../components/ActionBar'
import AddButton from '../components/AddButton'

const ParametrosPage: React.FC = () => {
  const queryClient = useQueryClient()
  
  // Estados para tabela
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('Todos')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  
  // Estados para dialogs
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<Configuracao | null>(null)
  
  // Formulário local
  const [chave, setChave] = useState('')
  const [valor, setValor] = useState('')
  const [tipo, setTipo] = useState<'string' | 'integer' | 'float' | 'boolean' | 'json'>('string')
  const [descricao, setDescricao] = useState('')
  const [categoria, setCategoria] = useState('')
  const [editavel, setEditavel] = useState(true)
  const [formError, setFormError] = useState<string | null>(null)
  
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Query para carregar parâmetros
  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['configuracoes'],
    queryFn: () => configuracoesApi.list(),
  })

  // Categorias únicas para filtro
  const categorias = useMemo(() => {
    const cats = new Set(configs.map((c) => c.categoria || 'Sem Categoria'))
    return ['Todos', ...Array.from(cats).sort()]
  }, [configs])

  // Filtragem local
  const filteredConfigs = useMemo(() => {
    return configs.filter((config) => {
      const matchesSearch =
        config.chave.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (config.descricao && config.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      
      const configCat = config.categoria || 'Sem Categoria'
      const matchesCategory = categoryFilter === 'Todos' || configCat === categoryFilter
      
      return matchesSearch && matchesCategory
    })
  }, [configs, searchTerm, categoryFilter])

  // Paginação
  const paginatedConfigs = filteredConfigs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: ConfiguracaoCreate) => configuracoesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuracoes'] })
      setSnackbar({ open: true, message: 'Configuração criada com sucesso', severity: 'success' })
      setDialogOpen(false)
    },
    onError: (err: any) => {
      setFormError(err.response?.data?.detail || 'Erro ao criar configuração')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ chave, data }: { chave: string; data: ConfiguracaoUpdate }) =>
      configuracoesApi.update(chave, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuracoes'] })
      setSnackbar({ open: true, message: 'Configuração atualizada com sucesso', severity: 'success' })
      setDialogOpen(false)
    },
    onError: (err: any) => {
      setFormError(err.response?.data?.detail || 'Erro ao atualizar configuração')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (chave: string) => configuracoesApi.delete(chave),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuracoes'] })
      setSnackbar({ open: true, message: 'Configuração excluída com sucesso', severity: 'success' })
      setConfirmDeleteOpen(false)
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Erro ao excluir configuração',
        severity: 'error',
      })
    },
  })

  const loading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  const handleOpenCreate = () => {
    setSelectedConfig(null)
    setChave('')
    setValor('')
    setTipo('string')
    setDescricao('')
    setCategoria('sistema')
    setEditavel(true)
    setFormError(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (config: Configuracao) => {
    setSelectedConfig(config)
    setChave(config.chave)
    setValor(config.valor)
    setTipo(config.tipo)
    setDescricao(config.descricao || '')
    setCategoria(config.categoria || '')
    setEditavel(config.editavel)
    setFormError(null)
    setDialogOpen(true)
  }

  const handleOpenDelete = (config: Configuracao) => {
    setSelectedConfig(config)
    setConfirmDeleteOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!chave.trim()) {
      setFormError('A chave é obrigatória')
      return
    }

    // Validar tipo de dado
    if (tipo === 'integer') {
      const num = parseInt(valor)
      if (isNaN(num)) {
        setFormError('O valor deve ser um número inteiro válido')
        return
      }
    } else if (tipo === 'float') {
      const num = parseFloat(valor)
      if (isNaN(num)) {
        setFormError('O valor deve ser um número decimal válido')
        return
      }
    } else if (tipo === 'json') {
      try {
        JSON.parse(valor)
      } catch (err) {
        setFormError('O valor deve ser um JSON válido')
        return
      }
    }

    if (selectedConfig) {
      // Edição
      const payload: ConfiguracaoUpdate = {
        valor,
        descricao: descricao || null,
        categoria: categoria || null,
        editavel,
      }
      updateMutation.mutate({ chave: selectedConfig.chave, data: payload })
    } else {
      // Criação
      const payload: ConfiguracaoCreate = {
        chave: chave.trim(),
        valor,
        tipo,
        descricao: descricao || null,
        categoria: categoria || null,
        editavel,
      }
      createMutation.mutate(payload)
    }
  }

  const handleDeleteConfirm = () => {
    if (selectedConfig) {
      deleteMutation.mutate(selectedConfig.chave)
    }
  }

  const getTipoColor = (t: string) => {
    switch (t) {
      case 'boolean': return 'success'
      case 'integer': return 'primary'
      case 'float': return 'secondary'
      case 'json': return 'warning'
      default: return 'default'
    }
  }

  return (
    <Box sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1.5, sm: 2, md: 3 }, width: '100%' }}>
      {/* Barra de Ações */}
      <ActionBar>
        <AddButton onClick={handleOpenCreate} title="Criar Parâmetro" />
      </ActionBar>

      {/* Barra de Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por chave ou descrição..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: { md: 400 } }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={categoryFilter}
            label="Categoria"
            onChange={(e) => {
              setCategoryFilter(e.target.value)
              setPage(0)
            }}
          >
            {categorias.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Grid de Parâmetros */}
      <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Chave</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Editável</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Carregando parâmetros...
                  </TableCell>
                </TableRow>
              ) : paginatedConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Nenhum parâmetro encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedConfigs.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                      {config.chave}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: config.tipo === 'json' ? 'monospace' : 'inherit' }}>
                      {config.valor}
                    </TableCell>
                    <TableCell>
                      <Chip label={config.tipo} size="small" color={getTipoColor(config.tipo) as any} />
                    </TableCell>
                    <TableCell>
                      <Chip label={config.categoria || 'Sem Categoria'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {config.descricao || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.editavel ? 'Sim' : 'Não'}
                        color={config.editavel ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEdit(config)}
                        disabled={!config.editavel}
                        title="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDelete(config)}
                        disabled={!config.editavel}
                        title="Excluir"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredConfigs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage="Registros por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>

      {/* Dialog para Criar/Editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSave}>
          <DialogTitle>
            {selectedConfig ? 'Editar Parâmetro' : 'Criar Novo Parâmetro'}
          </DialogTitle>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            <TextField
              label="Chave do Parâmetro"
              fullWidth
              margin="normal"
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              disabled={!!selectedConfig || loading}
              placeholder="ex: MAX_SESSIONS_PER_USER"
              helperText="Identificador único. Não use espaços ou caracteres especiais."
              required
            />

            {!selectedConfig && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Tipo de Dado</InputLabel>
                <Select
                  value={tipo}
                  label="Tipo de Dado"
                  onChange={(e) => {
                    setTipo(e.target.value as any)
                    setValor('')
                  }}
                  disabled={loading}
                >
                  <MenuItem value="string">String (Texto)</MenuItem>
                  <MenuItem value="integer">Integer (Número Inteiro)</MenuItem>
                  <MenuItem value="float">Float (Número Decimal)</MenuItem>
                  <MenuItem value="boolean">Boolean (Verdadeiro/Falso)</MenuItem>
                  <MenuItem value="json">JSON (Estrutura de dados)</MenuItem>
                </Select>
              </FormControl>
            )}

            {tipo === 'boolean' ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={valor === 'true'}
                    onChange={(e) => setValor(e.target.checked ? 'true' : 'false')}
                    disabled={loading}
                  />
                }
                label="Valor Ativo"
                sx={{ mt: 2, display: 'block' }}
              />
            ) : (
              <TextField
                label="Valor"
                fullWidth
                margin="normal"
                multiline={tipo === 'json' || tipo === 'string'}
                rows={tipo === 'json' ? 6 : 1}
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                disabled={loading}
                placeholder={tipo === 'json' ? '{\n  "config": true\n}' : 'Digite o valor...'}
                required
              />
            )}

            <TextField
              label="Categoria"
              fullWidth
              margin="normal"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              disabled={loading}
              placeholder="ex: autenticacao, seguranca"
              helperText="Usada para agrupar parâmetros na tela."
            />

            <TextField
              label="Descrição / Propósito"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              disabled={loading}
              placeholder="Explique o que este parâmetro controla e onde ele é utilizado..."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editavel}
                  onChange={(e) => setEditavel(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Editável via API"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || createMutation.isPending || updateMutation.isPending}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmação de Exclusão */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Excluir Parâmetro</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir permanentemente o parâmetro{' '}
          <strong>{selectedConfig?.chave}</strong>? Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleteMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toasts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ParametrosPage
